export const MAX_INBOX_ATTACHMENTS = 10;
export const MAX_INBOX_IMAGE_BYTES = 5 * 1024 * 1024;
export const MAX_INBOX_DOCUMENT_BYTES = 500 * 1024;

export const INBOX_ATTACHMENT_ACCEPT = [
  ".jpg",
  ".jpeg",
  ".png",
  ".pdf",
  ".vcf",
  "image/jpeg",
  "image/jpg",
  "image/png",
  "application/pdf",
  "text/vcard",
  "text/x-vcard",
  "text/directory",
  "application/vcard",
].join(",");

const ALLOWED_ATTACHMENT_TYPES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
  "application/pdf",
  "text/vcard",
  "text/x-vcard",
  "text/directory",
  "application/vcard",
]);

const ALLOWED_ATTACHMENT_EXTENSIONS = new Set([
  "jpg",
  "jpeg",
  "png",
  "pdf",
  "vcf",
]);

export interface InboxMessageAttachment {
  id: string;
  filename: string;
  contentType: string;
  size: number;
  sha256: string;
  status: string;
  downloadUrl?: string;
}

export interface InboxMediaUploadResponse {
  reference: string;
  attachment: Omit<InboxMessageAttachment, "status" | "downloadUrl">;
}

export interface SelectedInboxAttachment {
  key: string;
  file: File;
  reference?: string;
}

function normalizedContentType(contentType: string): string {
  return contentType.split(";", 1)[0].trim().toLowerCase();
}

function fileExtension(filename: string): string {
  const dotIndex = filename.lastIndexOf(".");
  return dotIndex < 0 ? "" : filename.slice(dotIndex + 1).toLowerCase();
}

function inboxAttachmentKind(
  file: Pick<File, "name" | "type">
): "image" | "document" | null {
  const contentType = normalizedContentType(file.type || "");
  if (
    contentType === "image/jpeg" ||
    contentType === "image/jpg" ||
    contentType === "image/png"
  ) {
    return "image";
  }
  if (
    contentType === "application/pdf" ||
    contentType === "text/vcard" ||
    contentType === "text/x-vcard" ||
    contentType === "text/directory" ||
    contentType === "application/vcard"
  ) {
    return "document";
  }

  const extension = fileExtension(file.name);
  if (extension === "jpg" || extension === "jpeg" || extension === "png") {
    return "image";
  }
  if (extension === "pdf" || extension === "vcf") return "document";
  return null;
}

export function isAllowedInboxAttachment(
  file: Pick<File, "name" | "type">
): boolean {
  const contentType = normalizedContentType(file.type || "");
  return (
    ALLOWED_ATTACHMENT_TYPES.has(contentType) ||
    ALLOWED_ATTACHMENT_EXTENSIONS.has(fileExtension(file.name))
  );
}

export function inboxAttachmentValidationError(
  file: Pick<File, "name" | "type" | "size">
): string | null {
  const kind = inboxAttachmentKind(file);
  if (!kind || !isAllowedInboxAttachment(file)) {
    return "Only JPEG, PNG, PDF, and vCard (.vcf) attachments are supported.";
  }
  const maxBytes =
    kind === "image" ? MAX_INBOX_IMAGE_BYTES : MAX_INBOX_DOCUMENT_BYTES;
  if (file.size > maxBytes) {
    return kind === "image"
      ? `${file.name} is larger than the 5 MB image limit.`
      : `${file.name} is larger than the 500 KB PDF/vCard limit.`;
  }
  if (file.size <= 0) return `${file.name} is empty.`;
  return null;
}

export function isImageInboxAttachment(
  attachment: Pick<InboxMessageAttachment, "contentType">
): boolean {
  const contentType = normalizedContentType(attachment.contentType);
  return (
    contentType === "image/jpeg" ||
    contentType === "image/jpg" ||
    contentType === "image/png"
  );
}

export function isProcessingInboxAttachment(
  attachment: Pick<InboxMessageAttachment, "status">
): boolean {
  return ["pending", "archiving", "processing"].includes(
    attachment.status.toLowerCase()
  );
}

export function mergeInboxMessageTimelines<
  T extends { id: string; createdAt: string }
>(current: T[], refreshed: T[]): T[] {
  const byId = new Map(current.map((message) => [message.id, message]));
  for (const message of refreshed) byId.set(message.id, message);
  return Array.from(byId.values()).sort((a, b) => {
    const timeDifference =
      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    return timeDifference || a.id.localeCompare(b.id);
  });
}

export function formatInboxAttachmentSize(size: number): string {
  if (!Number.isFinite(size) || size < 0) return "";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

export function getInboxMessageAttachments(
  traceMetadata: Record<string, unknown> | null | undefined
): InboxMessageAttachment[] {
  const rawAttachments = traceMetadata?.attachments;
  if (!Array.isArray(rawAttachments)) return [];

  return rawAttachments.flatMap((raw): InboxMessageAttachment[] => {
    if (!raw || typeof raw !== "object") return [];
    const candidate = raw as Record<string, unknown>;
    if (
      typeof candidate.id !== "string" ||
      !candidate.id ||
      typeof candidate.filename !== "string" ||
      !candidate.filename ||
      typeof candidate.contentType !== "string"
    ) {
      return [];
    }

    return [
      {
        id: candidate.id,
        filename: candidate.filename,
        contentType: normalizedContentType(candidate.contentType),
        size:
          typeof candidate.size === "number" &&
          Number.isFinite(candidate.size) &&
          candidate.size >= 0
            ? candidate.size
            : 0,
        sha256: typeof candidate.sha256 === "string" ? candidate.sha256 : "",
        status:
          typeof candidate.status === "string" ? candidate.status : "ready",
        ...(typeof candidate.downloadUrl === "string" &&
        candidate.downloadUrl.trim()
          ? { downloadUrl: candidate.downloadUrl.trim() }
          : {}),
      },
    ];
  });
}

export function hasProcessingInboxAttachments(
  messages: Array<{
    traceMetadata?: Record<string, unknown> | null;
  }>
): boolean {
  return messages.some((message) =>
    getInboxMessageAttachments(message.traceMetadata).some(
      isProcessingInboxAttachment
    )
  );
}

function joinApiUrl(baseUrl: string, path: string): string {
  return `${baseUrl.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;
}

export function buildInboxAttachmentUrl(
  baseUrl: string,
  messageId: string,
  attachment: Pick<InboxMessageAttachment, "id" | "downloadUrl">
): string {
  const fallbackPath = `/inbox/media/messages/${encodeURIComponent(
    messageId
  )}/attachments/${encodeURIComponent(attachment.id)}`;
  const candidate = attachment.downloadUrl?.trim();

  if (!candidate || candidate.startsWith("//")) {
    return joinApiUrl(baseUrl, fallbackPath);
  }

  if (/^https?:\/\//i.test(candidate)) {
    try {
      const candidateUrl = new URL(candidate);
      const apiUrl = new URL(baseUrl);
      if (candidateUrl.origin === apiUrl.origin) return candidateUrl.toString();
    } catch {
      // Fall through to the known authenticated endpoint.
    }
    return joinApiUrl(baseUrl, fallbackPath);
  }

  if (candidate.startsWith("/")) {
    try {
      const apiUrl = new URL(baseUrl);
      const apiPath = apiUrl.pathname.replace(/\/+$/, "");
      if (
        apiPath &&
        apiPath !== "/" &&
        (candidate === apiPath || candidate.startsWith(`${apiPath}/`))
      ) {
        return `${apiUrl.origin}${candidate}`;
      }
    } catch {
      // A relative API base will be handled by the normal join below.
    }
  }

  return joinApiUrl(baseUrl, candidate);
}

async function errorMessage(response: Response, fallback: string) {
  try {
    const body = await response.json();
    if (typeof body?.message === "string" && body.message.trim()) {
      return body.message;
    }
    if (typeof body?.error === "string" && body.error.trim()) {
      return body.error;
    }
  } catch {
    // The endpoint may return an empty or non-JSON error response.
  }
  return fallback;
}

export async function uploadInboxAttachment({
  baseUrl,
  token,
  file,
  signal,
}: {
  baseUrl: string;
  token: string;
  file: File;
  signal?: AbortSignal;
}): Promise<InboxMediaUploadResponse> {
  if (!token) throw new Error("Your session expired. Sign in and try again.");

  const formData = new FormData();
  formData.append("file", file);
  const response = await fetch(joinApiUrl(baseUrl, "/inbox/media"), {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
    signal,
  });

  if (!response.ok) {
    throw new Error(
      await errorMessage(response, `Could not upload ${file.name}.`)
    );
  }

  const result = (await response.json()) as Partial<InboxMediaUploadResponse>;
  if (
    typeof result.reference !== "string" ||
    !result.reference ||
    !result.attachment ||
    typeof result.attachment.id !== "string"
  ) {
    throw new Error(
      `The upload for ${file.name} returned an invalid response.`
    );
  }
  return result as InboxMediaUploadResponse;
}

export async function fetchInboxAttachmentBlob({
  baseUrl,
  token,
  messageId,
  attachment,
  signal,
}: {
  baseUrl: string;
  token: string;
  messageId: string;
  attachment: InboxMessageAttachment;
  signal?: AbortSignal;
}): Promise<Blob> {
  if (!token) throw new Error("Your session expired. Sign in and try again.");

  const response = await fetch(
    buildInboxAttachmentUrl(baseUrl, messageId, attachment),
    {
      headers: { Authorization: `Bearer ${token}` },
      signal,
    }
  );
  if (!response.ok) {
    throw new Error(
      await errorMessage(response, `Could not download ${attachment.filename}.`)
    );
  }
  return response.blob();
}
